/** Cylindo 3D viewer with skeleton loading & safety-net load timeout */

import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {Box, Skeleton, useBreakpointValue, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

const CONTROLS_DESKTOP = 'ar';
const CONTROLS_MOBILE_TABLET = 'ar nav';
// Safety-net "hide-skeleton" timer — runs only on the INITIAL mount for a given
// (code, customerId). Behaviour:
//
//   - If `ready` fires first → clear the timer, hide skeleton, fire onReady.
//   - If `error` fires first → flip hasError, fire onError (parent shows placeholder).
//   - If neither fires by LOAD_TIMEOUT_MS → assume the viewer is alive (its own
//     image will render whenever it's ready), hide our skeleton overlay AND fire
//     onReady so any parent skeleton (eg. ImageGalleryV2) hides too. Crucially we
//     do NOT flip hasError or call onError — the underlying <cylindo-viewer> web
//     component keeps loading underneath. This handles the warm-cache remount
//     case (eg. closing the "Choose your layout" panel) where Cylindo dispatches
//     `ready` synchronously inside connectedCallback BEFORE our addEventListener
//     can run, leaving the wrapper waiting for an event that will never come.
//     Real failures still surface immediately via the viewer's own `error` event.
//
// IMPORTANT: this timer only runs once per (code, customerId). `configuration`
// changes are handled by the separate "Configuration sync" effect below — Cylindo
// only fires `ready` on initial init, so re-arming on every config swap would
// always fall through to handleError() and break image transitions.
//
// 3 s is generous for cold-cache initial loads (typical `ready` fires <1 s) but
// short enough that warm-cache remounts don't strand the user behind a skeleton.
// There is no "Image not available" consequence anymore, so a short value is safe.
const LOAD_TIMEOUT_MS = 3_000;

// Set true to print lifecycle logs (mount, attribute changes, ready/error/timeout).
// Leave false in production.
const CYLINDO_DEBUG = false;

const log = (...args) => {
  if (CYLINDO_DEBUG && typeof console !== 'undefined') {
    console.log('[CylindoViewer]', ...args);
  }
};

const CylindoViewer = ({
  customerId,
  code,
  configuration,
  controls,
  onError,
  onReady,
  onZoomChange,
}) => {
  const viewerRef = useRef(null);
  const styles = useMultiStyleConfig('CylindoViewer', {});
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Latest callbacks accessible from the mount effect without re-subscribing
  // listeners (avoids the prior stale-closure issue when callbacks change identity).
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const onZoomChangeRef = useRef(onZoomChange);

  useEffect(() => {
    onReadyRef.current = onReady;
    onErrorRef.current = onError;
    onZoomChangeRef.current = onZoomChange;
  }, [onError, onReady, onZoomChange]);

  useLayoutEffect(() => {
    const viewer = viewerRef.current;

    if (!viewer?.locales?.en) return;

    viewer.locales = {
      ...viewer.locales,
      en: {
        ...viewer.locales.en,
        viewInAR: 'See It In Your Space',
      },
    };
  }, []);

  // Mount lifecycle — runs ONLY when (code, customerId) change.
  // - Resets loading state, attaches event listeners, arms the safety-net timer.
  // - The first `ready` clears the timer; the first `error` flips to placeholder.
  // - Configuration changes are handled by the separate "Configuration sync" effect
  //   below so we never re-arm the timer for a config swap that Cylindo will
  //   transition smoothly without firing a new `ready` event.
  useEffect(() => {
    const viewer = viewerRef.current;

    if (!viewer) return;

    log('mount/reset', {code, customerId});

    setIsLoading(true);
    setHasError(false);

    let readyFired = false;
    let errorFired = false;

    const handleReady = () => {
      if (readyFired) return;
      readyFired = true;
      log('ready', {code, customerId});
      setIsLoading(false);
      onReadyRef.current?.();
    };

    const handleError = (event) => {
      if (errorFired || readyFired) return;
      errorFired = true;
      log('error', {code, customerId, detail: event?.detail});
      setHasError(true);
      setIsLoading(false);
      onErrorRef.current?.();
    };

    const handleZoomChange = (event) => {
      const detail = event?.detail;
      const level = Number(detail?.zoom ?? detail ?? viewer.zoom);

      onZoomChangeRef.current?.(Number.isFinite(level) && level > 0 ? level : 1);
    };

    viewer.addEventListener('ready', handleReady);
    viewer.addEventListener('error', handleError);
    viewer.addEventListener('zoom-change', handleZoomChange);

    const timer = setTimeout(() => {
      if (!readyFired && !errorFired) {
        // Skeleton-hide only — never flip hasError from a timeout. See
        // LOAD_TIMEOUT_MS docs above for the reasoning. We call onReady() so the
        // parent (e.g. ImageGalleryV2) can also hide its own skeleton overlay;
        // the underlying <cylindo-viewer> will paint its image whenever it's
        // ready, even if the `ready` event was missed (warm-cache remount).
        log('safety-net timeout (hiding skeleton, viewer keeps loading)', {code, customerId, ms: LOAD_TIMEOUT_MS});
        readyFired = true;
        setIsLoading(false);
        onReadyRef.current?.();
      }
    }, LOAD_TIMEOUT_MS);

    return () => {
      viewer.removeEventListener('ready', handleReady);
      viewer.removeEventListener('error', handleError);
      viewer.removeEventListener('zoom-change', handleZoomChange);
      clearTimeout(timer);
    };
  }, [code, customerId]);

  // Configuration sync — runs on every `configuration` change AFTER mount.
  // We push the value to the web component via setAttribute (React already does
  // this through the JSX prop, but we also keep a debug log here so you can
  // see when configuration churns). NO timer, NO loading reset — Cylindo
  // updates the rendered frame internally without re-firing `ready`.
  useEffect(() => {
    if (!viewerRef.current) return;
    log('configuration change', {code, customerId, configuration});
  }, [configuration, code, customerId]);

  const responsiveControls = useBreakpointValue({
    base: CONTROLS_MOBILE_TABLET,
    md: CONTROLS_MOBILE_TABLET,
    xl: CONTROLS_DESKTOP,
  });

  const effectiveControls = controls ?? responsiveControls;
  const rootStyles = styles?.root ?? {};
  const {className, sx, ...rootRest} = rootStyles;

  if (hasError) return null;

  return (
    <Box className={className} sx={sx} {...rootRest}>
      {isLoading && (
        <Skeleton
          aspectRatio={256 / 123}
          height="auto"
          position="absolute"
          width="100%"
          zIndex={1}
        />
      )}
      {/* `key` keeps the underlying <cylindo-viewer> web component's identity
          tied to (code, customerId). When either changes (e.g. user switches
          to another master via "Choose your layout" → Done), React tears down
          and recreates the element so the third-party web component does not
          re-use any internal state from the previous product (which could
          otherwise leave a previously-failed frame visible). */}
      <cylindo-viewer
        key={`${code ?? ''}::${customerId ?? ''}`}
        ref={viewerRef}
        className="cylindo-viewer-component"
        code={code}
        configuration={configuration || undefined}
        controls={effectiveControls}
        customer-id={customerId}
        style={styles?.viewer ?? {aspectRatio: 256 / 123}}
        tabIndex={-1}
      />
    </Box>
  );
};

CylindoViewer.propTypes = {
  code: PropTypes.string.isRequired,
  configuration: PropTypes.string,
  controls: PropTypes.string,
  customerId: PropTypes.string.isRequired,
  onError: PropTypes.func,
  onReady: PropTypes.func,
  onZoomChange: PropTypes.func,
};

export default CylindoViewer;
